import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import styles from './Discounts.module.scss'
import { getOrderInProgress } from '../selectors/order'
import { getPromotions, getSelectedPromotions } from '../selectors/promotion'
import { addPromotionsToOrder } from '../actions/promotion'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import GratuityHeader from '../components/GratuityHeader'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import { centsToDollars } from '../utils/formatters'
import { useHistory } from 'react-router-dom'
import { addDiscountsToTabOrder } from '../actions/order'
import { isTabbed } from '../utils/orderStates'
import useSpinner from '../context/hooks/useSpinner'
import Discount from '../components/discounts/Discount'
import Button from '../components/Button'
import { sendUpdateCart } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import CartItemsViewModel from '../viewmodels/CartItemsViewModel'
import { getCartItems } from '../selectors/cart'
import { MODES } from '../VNMode/Reducer'
import { getDeviceMode } from '../VNMode/Selectors'
import { setManagerApprovalPrompt } from '../VNManagerApproval/ActionCreators'

const Discounts = ({ className }) => {
  const classNames = cn(styles.discounts, className)

  const dispatch = useDispatch()
  const history = useHistory()

  const discounts = useSelector((state) => getPromotions(state))
  const order = useSelector(getOrderInProgress)
  const selectedPromotions = useSelector((state) => getSelectedPromotions(state))
  const [selectedDiscounts, setSelectedDiscounts] = useState([...selectedPromotions])
  const isTabOrder = isTabbed(order)
  const total = history.location?.state?.total || 0
  const isMobileViewPort = useGetIsMobileScreen()
  const primaryText = !isMobileViewPort ? centsToDollars(total) : ''
  const fetchedCartItems = useSelector(getCartItems)
  const cartItems = CartItemsViewModel(fetchedCartItems)
  const cfdMode = useSelector(getDeviceMode)

  const [discountsChanged, setDiscountsChanged] = useState(false)
  const { startSpinner } = useSpinner()

  useEffect(() => {
    //need to display selected discounts in the CFD only
    if (cfdMode !== MODES.POS) {
      return
    }

    //we need to send items along with discounts to the CFD
    sendUpdateCart(cartItems, selectedDiscounts)
  }, [selectedDiscounts, cartItems, cfdMode])

  const handleApplyClick = () => {
    // check if discount needs manager approval
    let needsManagerApproval = false
    for (let i = 0; i < selectedDiscounts.length; i++) {
      if (selectedDiscounts[i]?.managerApproval) {
        needsManagerApproval = true
      }
    }

    if (needsManagerApproval) {
      dispatch(setManagerApprovalPrompt({ onSuccess: handleManagerApprovalSuccess, buttonText: 'Apply Discounts' }))
      return
    }

    handleManagerApprovalSuccess()
  }

  const handleManagerApprovalSuccess = (approverUuid = null) => {
    dispatch(addPromotionsToOrder({ selectedPromotions: selectedDiscounts, approverUuid }))

    if (isTabOrder) {
      //no need to stop spinner since tender selection will do it
      startSpinner()
      dispatch(addDiscountsToTabOrder())
      return
    }

    //no need to call addDiscountsToTabOrder when the order is not tabbed
    return history.push({
      pathname: '/tender-selection',
      state: { fromDiscounts: true, fromDiscountsCancel: false },
    })
  }

  const handleCancelClick = () => {
    return history.push({
      pathname: '/tender-selection',
      state: { fromDiscounts: true, fromDiscountsCancel: true },
    })
  }

  return (
    <div className={classNames}>
      <VNConcessionsNavBar showIcon={false} textDisplay={isMobileViewPort && centsToDollars(total)} />
      <GratuityHeader
        className={cn([styles.gratuityHeader, !isMobileViewPort && styles.gratuityPadding])}
        primaryText={primaryText}
        secondaryText="Please select discounts"
        isMobileViewPort={isMobileViewPort}
      />
      <div className={styles.discounts__body}>
        <div className={styles.discountSelections}>
          {discounts.map((discount, index) => {
            return (
              <Discount
                key={`${discount.uuid}-${index}`}
                discount={discount}
                index={index}
                selectedDiscounts={selectedDiscounts}
                setSelectedDiscounts={(selectedDiscounts) => {
                  setSelectedDiscounts(selectedDiscounts)
                  setDiscountsChanged(true)
                }}
              />
            )
          })}
        </div>
      </div>
      <div className={styles.buttonsContainer}>
        <div className={styles.buttonWrapper}>
          <Button useMui className={styles.button} onClick={handleCancelClick}>
            Cancel
          </Button>
          <Button useMui className={cn([styles.button, styles.primary])} onClick={handleApplyClick} disabled={!discountsChanged}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Discounts
